import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authAxios } from '../../api/authAxios';
import './profile.css';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, updateUser } = useAuth();

  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', email_notifications: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // { type: 'success'|'error', msg }

  // Redirect if not logged in
  useEffect(() => {
    if (!isAuthenticated) navigate('/login', { replace: true });
  }, [isAuthenticated, navigate]);

  // Load profile on mount
  useEffect(() => {
    if (!isAuthenticated) return;
    authAxios.get('profile/')
      .then(({ data }) => {
        setForm({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          email: data.email || '',
          phone: data.phone || '',
          email_notifications: !!data.email_notifications,
        });
      })
      .catch(() => setStatus({ type: 'error', msg: 'Failed to load profile. Please refresh.' }))
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    setStatus(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) {
      setStatus({ type: 'error', msg: 'Name is required.' });
      return;
    }
    setSaving(true);
    setStatus(null);
    try {
      const { data } = await authAxios.patch('profile/', {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim(),
        email_notifications: form.email_notifications,
      });
      // Sync updated name back into auth context
      updateUser({ first_name: data.first_name, last_name: data.last_name });
      setStatus({ type: 'success', msg: 'Profile saved successfully.' });
    } catch {
      setStatus({ type: 'error', msg: 'Failed to save. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="profile-page">
      <div className="profile-banner">
        <div className="container">
          <h1 className="profile-banner-title">My Profile</h1>
          <p className="profile-breadcrumb">
            <span onClick={() => navigate('/home')} className="profile-breadcrumb-link">Home</span>
            {' / '}My Profile
          </p>
        </div>
      </div>

      <div className="container profile-container">
        {loading ? (
          <div className="profile-loading">
            <span className="profile-spinner" />
            <span>Loading profile…</span>
          </div>
        ) : (
          <div className="profile-card">
            <div className="profile-avatar-block">
              <div className="profile-avatar-circle">
                {getInitials(form)}
              </div>
              <div className="profile-avatar-info">
                <p className="profile-avatar-name">
                  {[form.first_name, form.last_name].filter(Boolean).join(' ') || 'Your Name'}
                </p>
                <p className="profile-avatar-email">{form.email}</p>
              </div>
            </div>

            {status && (
              <div className={`profile-alert profile-alert--${status.type}`} role="alert">
                {status.msg}
              </div>
            )}

            <form onSubmit={handleSave} noValidate>
              <div className="profile-row">
                <div className="profile-field">
                  <label className="profile-label">First Name <span className="profile-required">*</span></label>
                  <input
                    className="profile-input"
                    type="text"
                    name="first_name"
                    value={form.first_name}
                    onChange={handleChange}
                    placeholder="First name"
                    required
                  />
                </div>
                <div className="profile-field">
                  <label className="profile-label">Last Name</label>
                  <input
                    className="profile-input"
                    type="text"
                    name="last_name"
                    value={form.last_name}
                    onChange={handleChange}
                    placeholder="Last name"
                  />
                </div>
              </div>

              <div className="profile-field">
                <label className="profile-label">Email <span className="profile-readonly-badge">Read-only</span></label>
                <input
                  className="profile-input profile-input--readonly"
                  type="email"
                  value={form.email}
                  readOnly
                  tabIndex={-1}
                />
              </div>

              <div className="profile-field">
                <label className="profile-label">Contact Number</label>
                <input
                  className="profile-input"
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="e.g. +91 98765 43210"
                />
              </div>

              <div className="profile-toggle-field">
                <label className="profile-toggle-label" htmlFor="email_notifications">
                  Receive email notifications
                </label>
                <button
                  type="button"
                  id="email_notifications"
                  role="switch"
                  aria-checked={form.email_notifications}
                  className={`profile-toggle ${form.email_notifications ? 'profile-toggle--on' : ''}`}
                  onClick={() => { setForm(f => ({ ...f, email_notifications: !f.email_notifications })); setStatus(null); }}
                >
                  <span className="profile-toggle-thumb" />
                </button>
              </div>

              <button
                type="submit"
                className="profile-save-btn"
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

function getInitials(form) {
  const f = (form.first_name || '').trim();
  const l = (form.last_name || '').trim();
  if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
  if (f) return f[0].toUpperCase();
  if (form.email) return form.email[0].toUpperCase();
  return '?';
}

export default ProfilePage;
